import React, { useEffect, useState } from "react";
import { ObjectPanelTabs } from "../co-panel/ObjectPanelComponent";
import { ChoiceGroup, Dropdown, IChoiceGroupOption, IconButton, ResponsiveMode, TeachingBubble } from "@fluentui/react";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { TextField } from "@fluentui/react/lib/TextField";
import { FormatPreview } from "../../../api/models/format-preview";
import { FormatTypeEnum } from "../../../api/models/format-type-enum";
import { formatPreview } from "../../../api/document/Format";
import { Label } from "@fluentui/react/lib/Label";
import { Toggle } from "@fluentui/react/lib/Toggle";
import {
  getDefaultBuiltInFormattingString,
  getDefaultCustomFormattingString,
} from "../../../utils/helpers/ObjectFormattingHelper";
import { builtInNumberFormat, builtInPercentageFormat } from "../../../utils/constants/formats";
import { InfoState } from "../../document/AdvancedDocumentPanel";
import { CoObjectAdvanced } from "../../interfaces/CoObject";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";

interface CoObjectAdvancedAmountProps {
  documentPartMetadata: DocumentPartMetadata;
  objectAdvanced: CoObjectAdvanced;
  updateCoObjectAdvancedAmount: (obj: CoObjectAdvanced, tabValid, modified: boolean) => void;
  isEdit: boolean;
  isLocked: boolean;
  isComponentInstance: boolean;
  tab: ObjectPanelTabs;
}

const DEFAULT_VALUE = "1234567.89";
const TYPE_DEFAULT = "Default";
const TYPE_CUSTOM = "Custom";

const getAdvancedAmount = (
  objectAdvanced: CoObjectAdvanced,
  isEdit: boolean,
  isComponentInstance: boolean,
  documentPartMetadata: DocumentPartMetadata
): CoObjectAdvanced | null => {
  if (isEdit || isComponentInstance) {
    return objectAdvanced;
  }

  return {
    choice: FormatTypeEnum.NUMBER,
    inheritFormat: false,
    formatMetadata: {
      numberFormatValue: getDefaultBuiltInFormattingString(FormatTypeEnum.NUMBER),
      numberType: TYPE_DEFAULT,
      percentageFormatValue: getDefaultBuiltInFormattingString(FormatTypeEnum.PERCENTAGE),
      percentageType: TYPE_DEFAULT,
      locale: documentPartMetadata?.formatMetadata?.locale,
    },
    language: documentPartMetadata?.language,
  };
};

export const CoObjectAdvancedAmountPanel: React.FunctionComponent<CoObjectAdvancedAmountProps> = (props) => {
  const { documentPartMetadata, objectAdvanced, updateCoObjectAdvancedAmount, isEdit, isComponentInstance, isLocked } =
    props;
  const [state, setState] = useState<{
    preview: string;
  }>({
    preview: "",
  });

  function validateFields(obj: CoObjectAdvanced): ObjectPanelTabs {
    let isValid = true;
    if (!obj) isValid = false;

    const { numberFormatValue, percentageFormatValue } = obj.formatMetadata;

    if (numberFormatValue == "") isValid = false;
    if (percentageFormatValue == "") isValid = false;

    return { key: props.tab.key, name: props.tab.name, isValid: isValid };
  }

  const updateFormatPreview = (newObjectAdvanced: CoObjectAdvanced) => {
    if (!newObjectAdvanced) {
      setState((s) => ({ ...s, preview: "" }));
      return;
    }
    const objectPreview: FormatPreview = {
      value: DEFAULT_VALUE,
      format:
        newObjectAdvanced.choice === FormatTypeEnum.NUMBER
          ? newObjectAdvanced.formatMetadata.numberFormatValue
          : newObjectAdvanced.formatMetadata.percentageFormatValue,
      typeFormat: FormatTypeEnum.NUMBER,
      typeFormatChoice:
        newObjectAdvanced.choice === FormatTypeEnum.NUMBER
          ? newObjectAdvanced.formatMetadata.numberType
          : newObjectAdvanced.formatMetadata.percentageType,
      locale: newObjectAdvanced.formatMetadata.locale,
    };
    formatPreview(objectPreview).then((value) => setState((s) => ({ ...s, preview: value })));
  };

  useEffect(() => {
    getInitialDocumentSettings();
  }, []);

  async function getInitialDocumentSettings() {
    const advancedAmount = getAdvancedAmount(objectAdvanced, isEdit, isComponentInstance, documentPartMetadata);
    updateCoObjectAdvancedAmount(advancedAmount, validateFields(advancedAmount), !isEdit);
    updateFormatPreview(advancedAmount);
  }

  const options: IChoiceGroupOption[] = [
    { key: FormatTypeEnum.NUMBER, text: formatMessage("cof.createnewobject.asnumber") },
    { key: FormatTypeEnum.PERCENTAGE, text: formatMessage("cof.createnewobject.aspercentage") },
  ];

  const onChangeFormatAs = (_: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption) => {
    objectAdvanced.choice = option.key as FormatTypeEnum;
    updateFormatPreview(objectAdvanced);
    updateCoObjectAdvancedAmount(objectAdvanced, validateFields(objectAdvanced), true);
  };

  const onChangeToggle = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
    const updatedAdvanced: CoObjectAdvanced = {
      choice: objectAdvanced.choice,
      inheritFormat: checked ?? false,
      formatMetadata: checked ? documentPartMetadata.formatMetadata : objectAdvanced.formatMetadata,
      language: documentPartMetadata?.language,
    };

    updateFormatPreview(updatedAdvanced);
    updateCoObjectAdvancedAmount(updatedAdvanced, validateFields(updatedAdvanced), true);
  };

  const getFormattingStringInputCaptionId = () => {
    switch (objectAdvanced?.choice) {
      case FormatTypeEnum.NUMBER:
        return formatMessage("cof.base.number");
      case FormatTypeEnum.PERCENTAGE:
        return formatMessage("cof.base.percentage");
    }
  };

  const optionsChoice = [
    { key: TYPE_DEFAULT, text: formatMessage("cof.base.default") },
    { key: TYPE_CUSTOM, text: formatMessage("cof.base.custom") },
  ];

  const onChangeFormattingString = (value, typeFormatChoice) => {
    if (objectAdvanced.choice === FormatTypeEnum.NUMBER) {
      objectAdvanced.formatMetadata = {
        ...objectAdvanced.formatMetadata,
        numberFormatValue: value,
        numberType: typeFormatChoice,
      };
    } else {
      objectAdvanced.formatMetadata = {
        ...objectAdvanced.formatMetadata,
        percentageFormatValue: value,
        percentageType: typeFormatChoice,
      };
    }

    updateFormatPreview(objectAdvanced);
    updateCoObjectAdvancedAmount(objectAdvanced, validateFields(objectAdvanced), true);
  };

  const infoId = "AdvancedDocumentPanelInfoId";

  const [stateInfo, setStateInfo] = useState<InfoState>({
    teachingBubbleVisible: false,
  });

  return (
    <>
      {stateInfo.teachingBubbleVisible && (
        <TeachingBubble
          calloutProps={{ calloutWidth: 300, backgroundColor: "lightgray" }}
          target={`#${infoId}`}
          hasCloseButton={true}
          closeButtonAriaLabel="Close"
          onDismiss={() => setStateInfo({ ...stateInfo, teachingBubbleVisible: false })}
          headline={formatMessage("cof.documentmanagement.outline.edit.advanced.formatoptions.info.title")}
        >
          <text>{formatMessage("cof.documentmanagement.outline.edit.advanced.formatoptions.info.message")}</text>
        </TeachingBubble>
      )}
      <div
        style={{
          fontSize: "1.15rem",
          fontWeight: "500",
          lineHeight: "1.25rem",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          color: "var(--color-blue)",
        }}
      >
        {formatMessage("cof.base.formatoptions")}
        <IconButton
          id={infoId}
          aria-label="Info"
          iconProps={{ iconName: "InfoSolid", style: { fontSize: "larger" } }}
          onClick={() => setStateInfo({ ...stateInfo, teachingBubbleVisible: !stateInfo.teachingBubbleVisible })}
        />
      </div>
      <div className="ms-Grid" dir="ltr">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              disabled={isComponentInstance || isLocked}
              selectedKey={objectAdvanced?.choice}
              defaultSelectedKey={FormatTypeEnum.NUMBER}
              styles={{
                flexContainer: { display: "flex", justifyContent: "left", columnGap: "0.5rem", flexWrap: "wrap" },
              }}
              options={options}
              onChange={onChangeFormatAs}
              label={formatMessage("cof.createnewobject.formatstarting")}
            />
          </div>
        </div>
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
            <Label style={{ float: "left" }}>{getFormattingStringInputCaptionId()}</Label>
          </div>
          <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
            <Toggle
              label={formatMessage("cof.createnewobject.inheritformat")}
              inlineLabel
              checked={objectAdvanced?.inheritFormat}
              onChange={onChangeToggle}
              disabled={isComponentInstance || isLocked}
              onText=" "
              offText=" "
            />
          </div>
        </div>
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
            <ChoiceGroup
              disabled={isComponentInstance || objectAdvanced?.inheritFormat || isLocked}
              selectedKey={
                objectAdvanced?.choice === FormatTypeEnum.NUMBER
                  ? (objectAdvanced?.formatMetadata?.numberType ?? TYPE_DEFAULT)
                  : (objectAdvanced?.formatMetadata?.percentageType ?? TYPE_DEFAULT)
              }
              styles={{
                flexContainer: {
                  display: "flex",
                  justifyContent: "left",
                  columnGap: "0.5rem",
                  flexWrap: "wrap",
                  marginTop: "-8px",
                  marginBottom: "4px",
                },
              }}
              options={optionsChoice}
              onChange={(_, choice) => {
                onChangeFormattingString(
                  choice.key === TYPE_CUSTOM
                    ? getDefaultCustomFormattingString(objectAdvanced?.choice)
                    : getDefaultBuiltInFormattingString(objectAdvanced?.choice),
                  choice.key
                );
              }}
            />
            {(objectAdvanced?.choice === FormatTypeEnum.NUMBER
              ? objectAdvanced?.formatMetadata?.numberType === TYPE_DEFAULT
              : objectAdvanced?.formatMetadata?.percentageType === TYPE_DEFAULT) && (
              <Dropdown
                disabled={isComponentInstance || objectAdvanced?.inheritFormat || isLocked}
                data-testid={"number-default"}
                options={
                  objectAdvanced?.choice === FormatTypeEnum.NUMBER ? builtInNumberFormat : builtInPercentageFormat
                }
                onChange={(_event, option) => {
                  onChangeFormattingString(option.key, objectAdvanced?.formatMetadata?.numberType);
                }}
                selectedKey={
                  objectAdvanced?.choice === FormatTypeEnum.NUMBER
                    ? builtInNumberFormat.find((f) => f.key == objectAdvanced?.formatMetadata?.numberFormatValue)?.key
                    : builtInPercentageFormat.find(
                        (f) => f.key == objectAdvanced?.formatMetadata?.percentageFormatValue
                      )?.key
                }
                responsiveMode={ResponsiveMode.large}
              />
            )}
            {(objectAdvanced?.choice === FormatTypeEnum.NUMBER
              ? objectAdvanced?.formatMetadata?.numberType === TYPE_CUSTOM
              : objectAdvanced?.formatMetadata?.percentageType === TYPE_CUSTOM) && (
              <TextField
                disabled={isComponentInstance || objectAdvanced?.inheritFormat || isLocked}
                value={
                  objectAdvanced?.choice === FormatTypeEnum.NUMBER
                    ? (objectAdvanced?.formatMetadata?.numberFormatValue ??
                      getDefaultCustomFormattingString(objectAdvanced?.choice))
                    : (objectAdvanced?.formatMetadata?.percentageFormatValue ??
                      getDefaultCustomFormattingString(objectAdvanced?.choice))
                }
                onChange={(e) =>
                  onChangeFormattingString(
                    (e.target as HTMLInputElement)?.value ?? "",
                    objectAdvanced?.choice === FormatTypeEnum.NUMBER
                      ? objectAdvanced?.formatMetadata?.numberType
                      : objectAdvanced?.formatMetadata?.percentageType
                  )
                }
              />
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ paddingLeft: "0.5rem", color: "lightgray" }}>{state.preview}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
